const getTelegramWebApp = () => {
    if (typeof window === "undefined" || !window.Telegram || !window.Telegram.WebApp) {
        return null;
    }

    return window.Telegram.WebApp;
};

// Возвращает значение launch-параметра из query/hash URL.
const getLaunchParam = (name) => {
    if (typeof window === "undefined") {
        return "";
    }

    const searchValue = new URLSearchParams(window.location.search || "").get(name);
    if (searchValue) {
        return searchValue;
    }

    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash) {
        return "";
    }

    return new URLSearchParams(hash).get(name) || "";
};

// Извлекает и декодирует initData из Telegram launch-параметров.
const getTelegramInitDataFromLaunchParams = () => {
    const raw = getLaunchParam("tgWebAppData");
    if (!raw) {
        return "";
    }

    try {
        return decodeURIComponent(raw);
    } catch (error) {
        return raw;
    }
};

// Парсит initData в initDataUnsafe-объект для fallback-режима.
const parseInitDataUnsafe = (initData) => {
    if (!initData) {
        return {};
    }

    try {
        const params = new URLSearchParams(initData);
        const userRaw = params.get("user");
        const user = userRaw ? JSON.parse(userRaw) : undefined;
        return {
            user,
            query_id: params.get("query_id") || "",
            start_param: params.get("start_param") || "",
            auth_date: params.get("auth_date") || "",
            hash: params.get("hash") || "",
        };
    } catch (error) {
        return {};
    }
};

export const isTelegramWebAppAvailable = () => Boolean(getTelegramWebApp());

// Инициализируем Telegram WebApp только если окружение доступно.
export const initTelegramWebApp = () => {
    const webApp = getTelegramWebApp();
    if (!webApp) {
        return false;
    }

    webApp.ready();
    webApp.expand();
    return true;
};

export const getTelegramPlatform = () => {
    const webApp = getTelegramWebApp();
    return webApp?.platform || "";
};

// Возвращаем initData из Telegram WebApp.
export const getTelegramInitData = () => {
    const webApp = getTelegramWebApp();
    if (webApp?.initData) {
        return webApp.initData;
    }

    return getTelegramInitDataFromLaunchParams();
};

export const getTelegramInitDataUnsafe = () => {
    const webApp = getTelegramWebApp();
    if (webApp?.initDataUnsafe && Object.keys(webApp.initDataUnsafe).length > 0) {
        return webApp.initDataUnsafe;
    }

    return parseInitDataUnsafe(getTelegramInitData());
};

export const getTelegramUser = () => {
    const initDataUnsafe = getTelegramInitDataUnsafe();
    return initDataUnsafe?.user || null;
};

export const getTelegramUserId = () => {
    const user = getTelegramUser();
    return user?.id || null;
};

export const getTelegramSafeAreaInsets = () => {
    const webApp = getTelegramWebApp();
    const insets = webApp?.safeAreaInset;
    if (!insets) {
        return undefined;
    }

    return {
        top: Number(insets.top) || 0,
        bottom: Number(insets.bottom) || 0,
        left: Number(insets.left) || 0,
        right: Number(insets.right) || 0,
    };
};

export const showTelegramBackButton = () => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton) {
        return;
    }

    backButton.show();
};

export const hideTelegramBackButton = () => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton) {
        return;
    }

    backButton.hide();
};

export const setTelegramBackButtonHandler = (handler) => {
    const backButton = getTelegramWebApp()?.BackButton;
    if (!backButton || typeof handler !== "function") {
        return () => {};
    }

    backButton.onClick(handler);

    return () => {
        backButton.offClick(handler);
    };
};

export const triggerTelegramHapticNotification = (type) => {
    const hapticFeedback = getTelegramWebApp()?.HapticFeedback;
    if (!hapticFeedback?.notificationOccurred) {
        return;
    }

    hapticFeedback.notificationOccurred(type);
};

export const openTelegramShare = ({text = "", url = ""}) => {
    const webApp = getTelegramWebApp();
    if (!webApp?.openTelegramLink) {
        return false;
    }

    const resolvedUrl = url || "https://t.me";
    const shareUrl = "https://t.me/share/url?url="
        + encodeURIComponent(resolvedUrl)
        + "&text="
        + encodeURIComponent(text);
    webApp.openTelegramLink(shareUrl);
    return true;
};

export const requestTelegramWriteAccess = () => {
    const webApp = getTelegramWebApp();
    if (!webApp?.requestWriteAccess) {
        return Promise.resolve(true);
    }

    return new Promise((resolve) => {
        webApp.requestWriteAccess((isAllowed) => {
            resolve(Boolean(isAllowed));
        });
    });
};
